import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ShieldIcon, PlusIcon, AlertTriangleIcon } from 'lucide-react'
import { useState } from 'react'

import { firewallApi } from '@nebula/shared/services'
import instancesApi, { type VMInstanceDetails } from '@nebula/shared/services/instancesApi'
import type {
  FirewallRule,
  FirewallRuleCreate,
  FirewallRuleUpdate,
} from '@nebula/shared/types/firewall'
import { getApiErrorText } from '@nebula/shared/utils'

import {
  FirewallRulesTable,
  CreateFirewallRuleModal,
  EditFirewallRuleModal,
  FirewallOptionsCard,
} from '../Firewall'

/**
 * A resposta do create/update traz o RECURSO, e um recurso pode chegar com
 * `sync_error`: o dual-write grava no banco e tenta aplicar no Proxmox. Dizer "criada
 * com sucesso" nesse caso é mentir — a regra existe aqui e NÃO no firewall. É o mesmo
 * tratamento que a aba do gateway já faz (`gateway/FirewallRulesTab.tsx`), e que faltava
 * aqui (#802).
 *
 * O backend novo nem chega a persistir a regra recusada (devolve 4xx e a mutation
 * rejeita), mas Fly e Pages deployam separados: durante a janela, este front conversa
 * com o backend antigo, que devolve 201 com `sync_error` preenchido.
 */
const falhaDeSincronizacao = (regra?: FirewallRule | null): string | null =>
  regra?.sync_error ? regra.sync_error : null

type FirewallTabProps = {
  instance: VMInstanceDetails
  /**
   * O firewall do CLUSTER desta instância está ligado? (#923) A página só monta esta aba
   * quando ele não é `false`. `null` = o portal não conseguiu saber (inclusive a consulta ter
   * falhado — p.ex. backend anterior à rota, que responde 404) — a aba fica, com aviso;
   * `undefined` = ainda carregando: nada a dizer.
   */
  clusterFirewallEnabled?: boolean | null
}

type VMInterface = {
  name: string
  network: string
  network_type: string
}

export default function FirewallTab({ instance, clusterFirewallEnabled }: FirewallTabProps) {
  const queryClient = useQueryClient()
  const vmId = instance.id

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingRule, setEditingRule] = useState<FirewallRule | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  // Regra criada que o Proxmox aceitou mas que não está em vigor.
  const [avisoDeEfetividade, setAvisoDeEfetividade] = useState<string | null>(null)
  // Falha ao ligar o firewall pelo botão do aviso.
  const [erroAoAtivar, setErroAoAtivar] = useState<string | null>(null)
  // Confirmação após ligar o firewall da VM pelo aviso.
  const [firewallAtivadoAqui, setFirewallAtivadoAqui] = useState(false)

  // Fetch firewall rules
  const { data: firewallRulesData, isLoading: isLoadingRules } = useQuery({
    queryKey: ['firewall-rules', vmId],
    queryFn: () => firewallApi.listFirewallRules(vmId),
    staleTime: 10_000,
  })

  // Fetch firewall options
  const { data: firewallOptions, isLoading: isLoadingOptions } = useQuery({
    queryKey: ['firewall-options', vmId],
    queryFn: () => firewallApi.getFirewallOptions(vmId),
    staleTime: 10_000,
  })

  // Create rule mutation
  const createRuleMutation = useMutation({
    mutationFn: (rule: FirewallRuleCreate) => firewallApi.createFirewallRule(vmId, rule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firewall-rules', vmId] })
    },
  })

  // Update rule mutation
  const updateRuleMutation = useMutation({
    mutationFn: ({ ruleId, rule }: { ruleId: string; rule: FirewallRuleUpdate }) =>
      firewallApi.updateFirewallRule(vmId, ruleId, rule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firewall-rules', vmId] })
    },
  })

  // Delete rule mutation
  const deleteRuleMutation = useMutation({
    mutationFn: (ruleId: string) => firewallApi.deleteFirewallRule(vmId, ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firewall-rules', vmId] })
    },
  })

  // Toggle firewall mutation
  const toggleFirewallMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      enabled ? firewallApi.enableFirewall(vmId) : firewallApi.disableFirewall(vmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firewall-options', vmId] })
      queryClient.invalidateQueries({ queryKey: ['firewall-rules', vmId] })
    },
  })

  // Update options mutation
  const updateOptionsMutation = useMutation({
    mutationFn: (options: any) => firewallApi.updateFirewallOptions(vmId, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firewall-options', vmId] })
    },
  })

  // Sync with Proxmox mutation
  const syncMutation = useMutation({
    mutationFn: () => firewallApi.syncFirewallRules(vmId, 'from_proxmox'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firewall-rules', vmId] })
      queryClient.invalidateQueries({ queryKey: ['firewall-options', vmId] })
    },
  })

  // Reorder rules mutation
  const reorderRulesMutation = useMutation({
    mutationFn: (ruleIds: string[]) => firewallApi.reorderFirewallRules(vmId, ruleIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firewall-rules', vmId] })
    },
  })

  // Handlers
  const handleCreateRule = async (rule: FirewallRuleCreate) => {
    const criada = await createRuleMutation.mutateAsync(rule)

    const erro = falhaDeSincronizacao(criada)
    if (erro) {
      // Propagado de propósito: é o `throw` que mantém o modal ABERTO com o erro. Sem
      // ele o modal fechava como sucesso e o aviso só aparecia numa faixa no rodapé da
      // tabela, para quem reparasse.
      throw new Error(`A regra foi salva, mas não foi possível aplicá-la à VM: ${erro}`)
    }

    // `=== false` e não `!criada.effective`: `undefined` (backend anterior ao campo) e
    // `null` (não se conseguiu ler o Proxmox) são "não sei", e "não sei" não vira aviso.
    setAvisoDeEfetividade(
      criada?.effective === false
        ? criada.effectiveness_reason ||
            'A regra foi criada, mas não está em vigor: o firewall desta VM está desligado.'
        : null,
    )
  }

  const handleUpdateRule = async (ruleId: string, rule: FirewallRuleUpdate) => {
    const atualizada = await updateRuleMutation.mutateAsync({ ruleId, rule })

    const erro = falhaDeSincronizacao(atualizada)
    if (erro) {
      throw new Error(`A alteração foi salva, mas não foi possível aplicá-la à VM: ${erro}`)
    }

    // Mesmo aviso da criação: editar uma regra numa VM com o firewall desligado também
    // produz uma regra que não filtra nada.
    setAvisoDeEfetividade(
      atualizada?.effective === false
        ? atualizada.effectiveness_reason ||
            'A regra foi salva, mas não está em vigor: o firewall desta VM está desligado.'
        : null,
    )
  }

  const handleDeleteRule = async (rule: FirewallRule) => {
    await deleteRuleMutation.mutateAsync(rule.id)
  }

  const handleToggleRule = async (rule: FirewallRule) => {
    // Pelo `handleUpdateRule`, não direto na mutation: habilitar/desabilitar é uma
    // edição como outra qualquer, e precisa das mesmas duas checagens — `sync_error`
    // (o Proxmox recusou) e `effective` (aplicou, mas não filtra nada).
    await handleUpdateRule(rule.id, { enabled: !rule.enabled })
  }

  const handleEditRule = (rule: FirewallRule) => {
    setEditingRule(rule)
    setShowEditModal(true)
  }

  const handleReorderRules = async (ruleIds: string[]) => {
    await reorderRulesMutation.mutateAsync(ruleIds)
  }

  // Fetch NICs via API (same pattern as NetworkTab)
  const { data: nicsData } = useQuery({
    queryKey: ['instance-nics', vmId],
    queryFn: () => instancesApi.listNICs(vmId),
    staleTime: 30_000,
  })

  // Derive network interfaces from NICs data
  const vmInterfaces: VMInterface[] =
    nicsData?.nics?.map((nic) => ({
      name: nic.net_id || 'net0',
      network: nic.bridge || nic.vpc?.name || 'unknown',
      network_type: nic.bridge?.startsWith('vmbr') ? 'bridge' : 'sdn_vnet',
    })) || []

  // Check if VM is stopped
  const isVmStopped = instance.status !== 'running'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center text-lg font-semibold text-gray-900">
            <ShieldIcon className="mr-2 size-5 text-purple-600" />
            Regras de Firewall
          </h2>
          <p className="text-sm text-gray-600">
            Configure regras de segurança para controlar o tráfego de rede desta VM
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
        >
          <PlusIcon className="size-4" />
          <span>Nova Regra</span>
        </button>
      </div>

      {/*
        Fica logo abaixo do cabeçalho, e não no rodapé da tabela: o cliente acabou de
        fechar o modal e está olhando para o topo da aba. Um aviso que nasce fora da
        viewport troca "parece que funcionou" por "ninguém vê".
      */}
      {avisoDeEfetividade && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-amber-600" />
            <div className="flex-1">
              <p className="text-sm text-amber-800">{avisoDeEfetividade}</p>
              <button
                onClick={async () => {
                  setErroAoAtivar(null)
                  try {
                    // Com `catch`: sem ele, um 400/503 rejeitava a promise dentro do
                    // handler de clique e nada aparecia — o aviso continuava na tela e o
                    // cliente não recebia nem o motivo do backend nem confirmação de
                    // desfecho desconhecido. O modal de criação já trata assim.
                    await toggleFirewallMutation.mutateAsync(true)
                    // Some o aviso e entra a confirmação — mas ela NÃO afirma que a
                    // regra passou a valer. No Proxmox a efetividade depende de três
                    // camadas (datacenter, NIC e VM) e o backend confere a da VM; dizer
                    // "pronto, está protegido" com uma conferida seria a mesma falsa
                    // sensação de proteção que esta issue elimina.
                    setAvisoDeEfetividade(null)
                    setFirewallAtivadoAqui(true)
                  } catch (error) {
                    setErroAoAtivar(
                      getApiErrorText(error) ||
                        (error as Error)?.message ||
                        'Não foi possível ativar o firewall',
                    )
                  }
                }}
                disabled={toggleFirewallMutation.isPending}
                className="mt-2 inline-flex items-center rounded-lg bg-amber-100 px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-200 disabled:opacity-50"
              >
                {toggleFirewallMutation.isPending ? 'Ativando...' : 'Ativar firewall agora'}
              </button>
              {erroAoAtivar && <p className="mt-2 text-sm text-red-700">{erroAoAtivar}</p>}
            </div>
            <button
              onClick={() => setAvisoDeEfetividade(null)}
              className="text-sm text-amber-600 hover:text-amber-800"
              aria-label="Dispensar aviso"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {firewallAtivadoAqui && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <ShieldIcon className="mt-0.5 size-5 shrink-0 text-blue-600" />
            <p className="flex-1 text-sm text-blue-800">
              <strong>Firewall da VM ativado.</strong>
              {/* Com o cluster confirmado ligado (#923), a hipótese do datacenter não cabe. */}
              {clusterFirewallEnabled !== true && (
                <>
                  {' '}
                  Se as regras ainda não surtirem efeito, o firewall do datacenter pode estar
                  desligado no cluster do provedor — o portal não conseguiu verificar isso. Fale com
                  o suporte.
                </>
              )}
            </p>
            <button
              onClick={() => setFirewallAtivadoAqui(false)}
              className="text-sm text-blue-600 hover:text-blue-800"
              aria-label="Dispensar aviso"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/*
        #923: só o `false` esconde a aba (na página). `null` é "não sei" — o cliente pode
        gerenciar as regras, mas fica sabendo que elas talvez não entrem em vigor.
      */}
      {clusterFirewallEnabled === null && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-3">
            <AlertTriangleIcon className="size-5 shrink-0 text-amber-600" />
            <p className="text-sm text-amber-800">
              Não foi possível confirmar se o firewall do cluster está ativo. Se ele estiver
              desligado, as regras desta VM não terão efeito.
            </p>
          </div>
        </div>
      )}

      {/* Warning for stopped VMs */}
      {isVmStopped && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-3">
            <AlertTriangleIcon className="size-5 shrink-0 text-amber-600" />
            <div>
              <p className="text-sm text-amber-800">
                <strong>VM Parada:</strong> As regras de firewall serão aplicadas quando a VM for
                iniciada.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Firewall content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Firewall Options Card */}
        <div className="lg:col-span-1">
          <FirewallOptionsCard
            options={firewallOptions || null}
            isLoading={isLoadingOptions}
            onToggleFirewall={async (enabled) => {
              await toggleFirewallMutation.mutateAsync(enabled)
            }}
            onUpdateOptions={async (options) => {
              await updateOptionsMutation.mutateAsync(options)
            }}
            onSync={async () => {
              await syncMutation.mutateAsync()
            }}
          />
        </div>

        {/* Firewall Rules Table */}
        <div className="lg:col-span-2">
          <FirewallRulesTable
            rules={firewallRulesData?.rules || []}
            isLoading={isLoadingRules}
            onEdit={handleEditRule}
            onDelete={handleDeleteRule}
            onToggle={handleToggleRule}
            onReorder={handleReorderRules}
            emptyMessage={
              // `=== false` e não a truthiness: `null` é "não se leu o Proxmox", e
              // mandar o cliente ativar um firewall cujo estado se desconhece é o
              // mesmo palpite que o card e o modal deixaram de dar.
              firewallOptions?.enabled === false
                ? 'Ative o firewall para começar a adicionar regras.'
                : 'Nenhuma regra configurada. Clique em "Nova Regra" para criar.'
            }
          />
        </div>
      </div>

      {/* Create Rule Modal */}
      <CreateFirewallRuleModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateRule}
        vmName={instance.name}
        vmInterfaces={vmInterfaces}
        firewallEnabled={firewallOptions?.enabled}
        onEnableFirewall={async () => {
          await toggleFirewallMutation.mutateAsync(true)
        }}
      />

      {/* Edit Rule Modal */}
      <EditFirewallRuleModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setEditingRule(null)
        }}
        onSubmit={handleUpdateRule}
        rule={editingRule}
        vmName={instance.name}
        vmInterfaces={vmInterfaces}
      />
    </div>
  )
}
